import posthog from "posthog-js";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BASE_URL } from "../../../api/baseURL";
import { clearAllClaimFiles } from "../../../utils/claimFilesStorage";
import useFileClaimFlow from "../../../hooks/useFileClaimFlow";
import { selectUserPoliciesData } from "../../../redux/features/myPolicySlice";
import { resetFileClaimFlow } from "../../../redux/features/motorInsuranceFileClaimFlowSlice";
import {
  clearNewMotorFormData,
  selectNewMotorForm,
} from "../../../redux/features/newMotorFormSlice";
import AccidentInfoPage, { AccidentInfo } from "./accidentInfoPage";
import CreatedClaimPage from "./createdClaimPage";
import CustomerTypePage from "./CustomerTypePage";
import {
  ACCIDENT_INFO,
  BODILY_INJURY_YOUR_INFO,
  CLAIM_CREATED,
  CUSTOMER_TYPE,
  FILE_CLAIM_FLOW,
  FILE_CLAIM_VEHICLE_DAMAGE_FLOW,
  NON_SOLIDARITY_POLICY_PLANS_PAGE,
  POLICY_PLANS,
  POLICY_TYPE,
  PROPERTY_DAMAGE_YOUR_INFO,
  REPAIR_OR_CASH,
  REQUIRED_DOCS_INFO,
  REVIEW_AND_CONFIRM,
  UPLOAD_DOCS,
  YOUR_INFO,
} from "./data";
import BodilyInjuryYourInfo from "./NonSolidarityUser/BuildInjuryFlow/bodilyInjuryYourInfo";
import PropertyDamageYourInfo from "./NonSolidarityUser/PropertyDamageFlow/propertyDamageYourInfo";
import SelectPolicyPlans from "./NonSolidarityUser/selectPolicyPlans";
import PolicyPlansPage from "./policyPlansPage";
import PolicyTypePage from "./policyTypePage";
import RepairOrCashPage from "./repairOrCashPage";
import RequiredDocsInfoPage from "./requiredDocsInfoPage";
import ReviewAndConfirmPage from "./reviewAndConfirmPage";
import UploadDocStepPage from "./uploadDocStepPage";
import UploadDocsPage, {
  AccidentDocs,
  DriverLicencesDocs,
  ImpactedPersonCprDocs,
  IncidentInput,
  InsuranceCert,
  JudicialReportDocs,
  OtherDocs,
  OwnershipDocs,
  ProofAddress,
  PropertyAccidentPhotosDocs,
  PropertyOwner,
} from "./uploadDocsPage";
import useVistaSession from "./useVistaSession";
import YourInfoPage, { ClaimInfoPage } from "./yourInfoPage";

const FileClaimMotorPolicy = () => {
  const dispatch = useDispatch();

  // For Create Claim API
  const [selectedCustomer, setSelectCustOption] = useState(null);
  const [selectedType, setSelectedInsuredType] = useState("");
  const [selectedInsuredType, setSelectInsuredType] = useState(null);

  const [selectedTpType, setSelectTpType] = useState(null);

  const [selectedClaimType, setSelectClaimType] = useState(null);
  const {
    currentStep,
    currentSubstep,
    flowType,
    currentStepIndex,
    currentSubstepIndex,
  } = useFileClaimFlow();

  // Which way the wizard just moved, so the incoming page slides in from the
  // side it came from.
  //
  // Derived during render rather than in an effect: an effect runs after the
  // new page has already painted, so its first frame — the one the entrance
  // starts on — would still carry the previous direction.
  const flowPosition = currentStepIndex * 1000 + currentSubstepIndex;
  const navRef = useRef({
    position: flowPosition,
    flowType,
    direction: "forward",
  });
  if (
    flowPosition !== navRef.current.position ||
    flowType !== navRef.current.flowType
  ) {
    navRef.current = {
      position: flowPosition,
      flowType,
      // Picking a different flow restarts the wizard at 0,0, which reads as a
      // long way backwards by index while the customer has actually moved on.
      direction:
        flowType !== navRef.current.flowType ||
        flowPosition > navRef.current.position
          ? "forward"
          : "back",
    };
  }
  const navDirection = navRef.current.direction;
  const [garages, setGarages] = useState([]);
  //const [formData, setFormData] = useState({});
  const claimInfo = useSelector(selectNewMotorForm)?.[ClaimInfoPage];
  const propertyDamageInfo =
    useSelector(selectNewMotorForm)?.[PROPERTY_DAMAGE_YOUR_INFO];

  const AccidentInfoDetails = useSelector(selectNewMotorForm)?.[AccidentInfo];

  const driverDocs = useSelector(selectNewMotorForm)?.[DriverLicencesDocs];
  const accidentDocs = useSelector(selectNewMotorForm)?.[AccidentDocs];
  const ownerShipDocs = useSelector(selectNewMotorForm)?.[OwnershipDocs];
  const incidentInput = useSelector(selectNewMotorForm)?.[IncidentInput];
  const insuranceCert = useSelector(selectNewMotorForm)?.[InsuranceCert];
  const propertyOwner = useSelector(selectNewMotorForm)?.[PropertyOwner];
  const proofAddress = useSelector(selectNewMotorForm)?.[ProofAddress];
  const propertyAccidentPhotosDocs =
    useSelector(selectNewMotorForm)?.[PropertyAccidentPhotosDocs];
  const impactedPersonCprDocs =
    useSelector(selectNewMotorForm)?.[ImpactedPersonCprDocs];
  const judicialReportDocs =
    useSelector(selectNewMotorForm)?.[JudicialReportDocs];
  const otherDocs = useSelector(selectNewMotorForm)?.[OtherDocs];

  // Resolve the customer's real policy number by matching the entered plate
  // number against the logged-in user's motor policies (same match as quick
  // renewal). Null when unresolved (third-party claimant, policies not loaded).
  const userPolicies = useSelector(selectUserPoliciesData);
  const resolvedPolicyNumber = useMemo(() => {
    const plateNumber = claimInfo?.plateNumber;
    if (!plateNumber || !Array.isArray(userPolicies)) return null;
    const match = userPolicies.find(
      (policy) =>
        policy?.ProductCode === "0101" &&
        policy?.RegistrationNumber == plateNumber &&
        policy?.PolicyStatus !== "EXPIRED"
    );
    return match?.PolicyNumber || null;
  }, [userPolicies, claimInfo?.plateNumber]);

  // The wizard's Vista session: the ref every validate-only call rides on and
  // the cross-document findings made under it, accumulated across the
  // per-document upload pages so a conflict stays visible when navigating
  // Back.
  const {
    sessionRef: vistaSessionRef,
    crossChecks: vistaCrossChecks,
    setCrossChecks: setVistaCrossChecks,
    documentLimits: vistaDocumentLimits,
  } = useVistaSession();

  const GetGarages = async () => {
    const url = BASE_URL + "/constants/garages";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", // Set headers for JSON
        }, // Convert data to JSON string
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      //console.log("Success:", result);

      var test = result;
      return result;
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const isFeatureEnabled = posthog.isFeatureEnabled("Emergency_services");

  if (isFeatureEnabled) {
    console.log("Feature is enabled!");
    // Render the new feature
  } else {
    console.log("Feature is disabled!");
    // Render the fallback
  }

  useEffect(() => {
    // Clear any stale claim files from previous sessions when starting a new claim flow
    console.log("[FileClaimMotorPolicy] Clearing stale claim files on mount");
    clearAllClaimFiles();

    // Call GetGarages and set the state with the resolved result
    const fetchData = async () => {
      const garagesData = await GetGarages();
      setGarages(garagesData); // Set the data once it's resolved
    };

    fetchData(); // Call the async function
  }, []);

  // Use ref to track current step for cleanup (avoids running cleanup on every step change)
  const currentStepRef = useRef(currentStep?.name);
  useEffect(() => {
    currentStepRef.current = currentStep?.name;
  }, [currentStep?.name]);

  // Cleanup form data and reset flow when component unmounts (user navigates away without completing)
  // Note: File cleanup is handled in reviewAndConfirmPage.jsx after successful Vista submission
  useEffect(() => {
    return () => {
      // Only clear if not on the success page (claim was not submitted)
      if (currentStepRef.current !== CLAIM_CREATED) {
        console.log("[FileClaimMotorPolicy] Cleaning up form data and resetting flow on unmount");
        dispatch(clearNewMotorFormData());
        dispatch(resetFileClaimFlow());
        // Don't clear files here - Vista API call may still be in progress
        // Files are cleared in reviewAndConfirmPage.jsx after successful submission
      }
    };
  }, [dispatch]);

  const PageRenderer = () => {
    console.log("selectedType", selectedType);
    switch (currentStep?.name) {
      case CUSTOMER_TYPE:
        return (
          <CustomerTypePage
            selectedOption={selectedCustomer}
            setSelectedOption={setSelectCustOption}
          />
        );
      case POLICY_TYPE:
        return (
          <PolicyTypePage setSelectedInsuredType={setSelectedInsuredType} />
        );
      case REQUIRED_DOCS_INFO:
        return (
          <RequiredDocsInfoPage selectedInsuredType={selectedInsuredType} />
        );
      case POLICY_PLANS:
        return (
          <PolicyPlansPage
            selectedOption={selectedTpType}
            setSelectedOption={setSelectTpType}
          />
        );
      case NON_SOLIDARITY_POLICY_PLANS_PAGE:
        return (
          <SelectPolicyPlans
            selectedOption={selectedInsuredType}
            setSelectedOption={setSelectInsuredType}
          />
        );
      case YOUR_INFO:
        return <YourInfoPage />;
      case PROPERTY_DAMAGE_YOUR_INFO:
        return <PropertyDamageYourInfo />;
      case BODILY_INJURY_YOUR_INFO:
        return <BodilyInjuryYourInfo />;
      case ACCIDENT_INFO:
        return (
          <AccidentInfoPage
            garages={garages}
            selectedInsuredType={selectedInsuredType}
          />
        );
      case REPAIR_OR_CASH:
        return (
          <RepairOrCashPage
            selectedInsuredType={selectedInsuredType}
            selectedOption={selectedClaimType}
            setSelectedOption={setSelectClaimType}
          />
        );
      case UPLOAD_DOCS:
        // Solidarity motor flow and third-party vehicle damage: redesigned
        // one-document-per-page wizard (keyed by substep so each page mounts
        // fresh and hydrates its own document slot from redux). Property
        // damage / bodily injury keep the legacy single page.
        if (
          flowType === FILE_CLAIM_FLOW ||
          flowType === FILE_CLAIM_VEHICLE_DAMAGE_FLOW
        ) {
          return (
            <UploadDocStepPage
              key={currentSubstep}
              sessionRef={vistaSessionRef}
              policyNumber={resolvedPolicyNumber}
              driverCpr={claimInfo?.driverCprNumber}
              crossChecks={vistaCrossChecks}
              setCrossChecks={setVistaCrossChecks}
              documentLimits={vistaDocumentLimits}
            />
          );
        }
        return (
          <UploadDocsPage
            selectedType={selectedType}
            selectedInsuredType={selectedInsuredType}
            sessionRef={vistaSessionRef}
            policyNumber={resolvedPolicyNumber}
            driverCpr={claimInfo?.driverCprNumber}
          />
        );
      case REVIEW_AND_CONFIRM:
        return (
          <ReviewAndConfirmPage
            selectedCustomer={selectedCustomer}
            selectedInsuredType={selectedInsuredType}
            selectedClaimType={selectedClaimType}
            driverDocs={driverDocs}
            incidentDoc={incidentInput}
            accidentDocs={accidentDocs}
            ownerShipDocs={ownerShipDocs}
            AccidentInfoDetails={AccidentInfoDetails}
            claimInfo={claimInfo}
            insuranceCert={insuranceCert}
            propertyOwner={propertyOwner}
            proofAddress={proofAddress}
            impactedPersonCprDocs={impactedPersonCprDocs}
            judicialReportDocs={judicialReportDocs}
            otherDocs={otherDocs}
            propertyAccidentPhotosDocs={propertyAccidentPhotosDocs}
            selectedTpType={selectedTpType}
            propertyDamageInfo={propertyDamageInfo}
            sessionRef={vistaSessionRef}
            resolvedPolicyNumber={resolvedPolicyNumber}
          />
        );
      case CLAIM_CREATED:
        return <CreatedClaimPage />;
      default:
        return null;
    }
  };
  return (
    // The sliding wrapper is full width, so its own edge crosses the viewport
    // during the travel even though the page column inside it is a centred
    // 500px — margins do not absorb it. Without clamping, every forward step
    // flashes a horizontal scrollbar for 240ms.
    //
    // `clip` rather than `hidden` at the base: `overflow-x: hidden` beside an
    // unset `overflow-y` coerces y to `auto`, turning the wizard into a scroll
    // container and cropping any date picker that opens past its edge. `clip`
    // leaves y `visible`. The two breakpoints below already declare
    // `overflow-y: scroll`, so `hidden` is safe there and is understood by
    // Safari before 16, which is where an old phone is most likely to be.
    <div
      data-claim-nav={navDirection}
      className="mainCarDetailsModal fileAClaim pt-[5%] pb-[5%] overflow-x-clip max767:w-full max767:overflow-y-scroll max767:overflow-x-hidden max767:pb-[30%] max767:h-[95vh] ipad:pt-[10%] ipad:h-[93vh] ipad:overflow-y-scroll ipad:overflow-x-hidden"
    >
      {PageRenderer()}
    </div>
  );
};

export default FileClaimMotorPolicy;
